import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight, ChevronRight, Sparkles, X } from "lucide-react";
import { PROTOTYPES, ECO_FLOW } from "../../lib/site";
import { trackPrototypeOpen } from "../../lib/api";
import { trackEvent } from "../../lib/analytics";
import { Reveal, Stagger, StaggerItem } from "./Reveal";
import { SectionHeading } from "./Shared";

const PositioningBand = () => (
  <div className="max-w-7xl mx-auto px-5 sm:px-8">
    <Reveal>
      <SectionHeading
        overline="One Governed Operating Layer"
        title="One School. One Operating Ecosystem."
        sub="Instead of relying on disconnected ERPs, communication tools, spreadsheets and separate applications, PARTECON brings the school ecosystem together through one governed digital operating layer."
      />
    </Reveal>
    <Reveal delay={0.1}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-4 mt-10">
        {ECO_FLOW.map((step, i) => (
          <div key={step} className="flex items-center gap-3">
            <span className="glass rounded-full px-4 py-2 text-sm text-white font-medium whitespace-nowrap">{step}</span>
            {i < ECO_FLOW.length - 1 && <ChevronRight size={16} className="text-[#6366F1] shrink-0" />}
          </div>
        ))}
      </div>
    </Reveal>
  </div>
);

const HubMotif = () => (
  <div className="flex flex-col items-center mt-24 mb-4">
    <Reveal>
      <div className="relative inline-flex items-center gap-2.5 glass rounded-full px-6 py-3 border-[#6366F1]/30">
        <span className="h-2.5 w-2.5 rounded-full bg-[#6366F1] animate-pulse-node" />
        <span className="font-heading text-sm font-semibold tracking-[0.2em] text-white">PARTECON · SCHOOL OS</span>
      </div>
    </Reveal>
    <div className="h-16 w-px bg-gradient-to-b from-[#6366F1]/60 to-transparent mt-1" />
  </div>
);

export const ExploreEcosystem = () => {
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("partecon_tour_seen")) setShowTour(true);
  }, []);

  const dismissTour = () => {
    localStorage.setItem("partecon_tour_seen", "1");
    setShowTour(false);
  };

  const handleOpen = (p) => {
    trackPrototypeOpen(p.key);
    trackEvent("prototype_open", { prototype_key: p.key, prototype_name: p.name });
    dismissTour();
    if (p.link) window.open(p.link, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="explore" className="py-28 md:py-36 relative bg-[#0E1117]" data-testid="explore-section">
      <div className="absolute inset-0 grid-lines opacity-40" />
      <div className="relative">
        <PositioningBand />

        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <HubMotif />

          <Reveal>
            <SectionHeading center overline="Interactive Product"
              title="Experience PARTECON"
              sub="Explore how PARTECON is designed for every stakeholder in a modern school." />
          </Reveal>

          {showTour && (
            <div className="flex justify-center mt-8" data-testid="tour-hint">
              <div className="glass rounded-full pl-4 pr-2 py-2 flex items-center gap-3 border-[#6366F1]/40">
                <Sparkles size={15} className="text-[#6366F1]" />
                <span className="text-sm text-white">New here? <span className="text-[#6366F1]">Start with the Principal Dashboard</span> prototype.</span>
                <button onClick={dismissTour} data-testid="tour-dismiss" className="h-6 w-6 rounded-full hover:bg-white/10 flex items-center justify-center text-[#9DA9BA] transition-colors"><X size={13} /></button>
              </div>
            </div>
          )}

          <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-16">
            {PROTOTYPES.map((p) => (
              <StaggerItem key={p.key}>
                <div className={`relative glass glass-hover rounded-3xl p-6 h-full flex flex-col group ${p.key === "principal" && showTour ? "ring-1 ring-[#6366F1]/60" : ""}`} data-testid={`prototype-card-${p.key}`}>
                  {p.key === "principal" && showTour && (
                    <span className="absolute -top-3 left-6 z-10 inline-flex items-center gap-1.5 bg-[#6366F1] text-[#0E1117] text-[10px] font-mono-accent uppercase tracking-wider rounded-full px-3 py-1 animate-pulse-node" data-testid="start-here-badge">
                      <Sparkles size={11} /> Start here
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="h-10 w-10 rounded-xl bg-[#6366F1]/10 flex items-center justify-center">
                        <p.icon size={19} className="text-[#6366F1]" />
                      </span>
                      <div>
                        <p className="overline !text-[10px]">{p.tag}</p>
                        <h3 className="font-heading text-lg font-medium leading-tight">{p.name}</h3>
                      </div>
                    </div>
                    <span className="text-[9px] font-mono-accent uppercase tracking-wider text-[#A5B4FC] border border-white/10 rounded-full px-2.5 py-1 whitespace-nowrap">Interactive Prototype</span>
                  </div>

                  <p className="text-[#9DA9BA] text-sm mt-4 leading-relaxed">{p.desc}</p>

                  <div className={`rounded-xl overflow-hidden mt-5 border border-white/10 relative bg-[#0E1117] ${p.wide ? "h-44" : "h-56"}`}>
                    <div className="absolute inset-0 grid-lines opacity-40" />
                    <div className="absolute inset-0 radial-glow" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 transition-transform duration-700 group-hover:scale-[1.04]">
                      <span className="h-14 w-14 rounded-2xl bg-white/[0.06] border border-white/10 flex items-center justify-center backdrop-blur-sm">
                        <p.icon size={26} className="text-[#A5B4FC]" />
                      </span>
                      <span className="text-white/70 text-xs font-mono-accent tracking-widest uppercase">{p.tag}</span>
                      <span className="text-white/35 text-[10px] font-mono-accent">partecon smart school</span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0E1117]/60 to-transparent" />
                  </div>

                  <button onClick={() => handleOpen(p)} data-testid={`prototype-cta-${p.key}`}
                    className="mt-6 inline-flex items-center justify-between gap-2 btn-primary px-5 py-3 text-sm w-full">
                    <span>Explore Prototype</span>
                    <ExternalLink size={15} />
                  </button>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>

        {/* Bottom CTA */}
        <div className="max-w-4xl mx-auto px-5 sm:px-8 mt-24">
          <Reveal>
            <div className="glass rounded-3xl p-10 md:p-14 text-center border-[#6366F1]/20" data-testid="explore-cta">
              <span className="overline">See PARTECON in Action</span>
              <h3 className="font-heading text-3xl md:text-4xl tracking-tight font-medium mt-4">See PARTECON in Action</h3>
              <p className="text-[#9DA9BA] mt-4 max-w-xl mx-auto leading-relaxed">
                Explore the interactive prototypes and experience how PARTECON is designed for every stakeholder in a modern school.
              </p>
              <Link to="/product" data-testid="explore-product-btn"
                className="btn-primary px-8 py-4 text-sm inline-flex items-center gap-2 mt-8">
                Explore the Product <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
